import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CalendarIcon, TagIcon, Trash2Icon, TruckIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useDeleteExpense, useExpenseCategories, useExpenses } from '@/api/expenses'
import type { Expense, ExpenseCategory } from '@/api/expenses'
import { membershipFor, useMe } from '@/api/me'
import { useWorkspace } from '@/api/workspace'
import { VoidedBadge } from '@/components/corrections/voided'
import { CsvExportButton } from '@/components/csv-export/csv-export-button'
import { categoryLabel } from '@/components/expenses/category-label'
import { monthOf, todayIn } from '@/components/expenses/dates'
import { DeleteExpenseDialog } from '@/components/expenses/delete-dialog'
import { expenseErrorMessage } from '@/components/expenses/error-message'
import { Chip, FilterBar, ValuePopover } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// Expenses list (UI-SCREENS E-01, 07a #194): date, category, amount, vendor, note and who
// recorded it, newest business date first, one month at a time. The Owner sees every Expense of
// the Workspace and Staff only their own — the API decides, not this screen.
//
// Two endings look different (owner decision 2026-09-23, Q3): a Restock Expense voided with its
// Restock stays visible, greyed, with a badge that links to M-06; a soft-deleted manual Expense
// is gone unless the Owner asks for it.
//
// The filters sit in the shared filter bar (as on I-03) and live in the URL. The month is always
// set, so it is a chip without × that only changes month; without `?month=` it is this month.
const searchSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/).optional().catch(undefined),
  categoryId: z.coerce.number().int().positive().optional().catch(undefined),
  deleted: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/expenses/')({
  component: ExpensesList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'expenses.title' },
})

function ExpensesList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)

  if (workspace.isPending) return <LoadingSkeleton variant="page" />
  if (workspace.isError || !workspace.data)
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )

  return <ExpensesScreen slug={slug} thisMonth={monthOf(todayIn(workspace.data.timezone))} />
}

function ExpensesScreen({ slug, thisMonth }: { slug: string; thisMonth: string }) {
  const { t } = useTranslation()
  const me = useMe()
  const isOwner = membershipFor(me.data, slug)?.role === 'owner'
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  // The current month with every category, which is what the monthly summary counts (Q4).
  const month = search.month ?? thisMonth
  const categories = useExpenseCategories(slug)
  const expenses = useExpenses(slug, { month, categoryId: search.categoryId, deleted: isOwner && search.deleted })
  const rows = expenses.data ?? []
  const total = rows
    .filter((expense) => !expense.voided_at && !expense.deleted_at)
    .reduce((sum, expense) => sum + expense.amount, 0)

  function set(patch: Partial<typeof search>) {
    navigate({ search: (current) => ({ ...current, ...patch }), replace: true })
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('expenses.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* The fifth and last phase-1 export (#197), Owner only: the month and category on
              screen, one row per Expense with every ending. */}
          {isOwner ? (
            <CsvExportButton
              slug={slug}
              list="expenses"
              query={{ month, category_id: search.categoryId }}
            />
          ) : null}
          <Button asChild className="h-11">
            <Link to="/w/$slug/expenses/new" params={{ slug }}>
              {t('expenses.new')}
            </Link>
          </Button>
        </div>
      </div>

      <FilterBar
        fields={[
          {
            key: 'categoryId',
            label: t('expenses.filterCategory'),
            icon: TagIcon,
            value: search.categoryId ? String(search.categoryId) : undefined,
            options: (categories.data ?? []).map((category: ExpenseCategory) => ({
              value: String(category.id),
              label: categoryLabel(t, category.name),
            })),
            onChange: (value) => set({ categoryId: value === undefined ? undefined : Number(value) }),
          },
          // Soft-deleted Expenses are the Owner's to see (Q3).
          ...(isOwner
            ? [
                {
                  key: 'deleted',
                  label: t('expenses.filterDeleted'),
                  icon: Trash2Icon,
                  value: search.deleted ? 'true' : undefined,
                  options: [{ value: 'true', label: t('expenses.deletedShown') }],
                  onChange: (value: string | undefined) => set({ deleted: value === 'true' ? true : undefined }),
                },
              ]
            : []),
        ]}
        extraActive={month !== thisMonth}
        onClearAll={() => set({ month: undefined, categoryId: undefined, deleted: undefined })}
      >
        <Chip icon={CalendarIcon} label={t('expenses.filterMonth')}>
          <ValuePopover
            label={t('expenses.filterMonth')}
            value={formatMonth(month)}
            content={() => (
              <div className="p-2">
                <Input
                  type="month"
                  aria-label={t('expenses.filterMonth')}
                  className="h-9"
                  value={month}
                  onChange={(event) =>
                    set({ month: event.target.value && event.target.value !== thisMonth ? event.target.value : undefined })
                  }
                />
              </div>
            )}
          />
        </Chip>
      </FilterBar>

      {expenses.isPending ? (
        <LoadingSkeleton />
      ) : expenses.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : rows.length === 0 ? (
        <RecordListEmpty>{t('expenses.empty')}</RecordListEmpty>
      ) : (
        <>
          <p className="text-base font-medium">
            {t('expenses.total')}: Rp {formatMoney(total)}
          </p>
          <RecordList>
            {rows.map((expense) => (
              <ExpenseRow key={expense.id} slug={slug} expense={expense} />
            ))}
          </RecordList>
        </>
      )}
    </section>
  )
}

// 2026-10 → "Oktober 2026".
function formatMonth(month: string) {
  return new Intl.DateTimeFormat('id-ID', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${month}-01T00:00:00Z`),
  )
}

// One Expense: the business date as the title (an Expense has no number and no detail page), the
// amount as the figure, the note on its own line. Edit, delete and the Restock link stay on the
// row; a voided or deleted Expense is greyed.
function ExpenseRow({ slug, expense }: { slug: string; expense: Expense }) {
  const { t } = useTranslation()
  const remove = useDeleteExpense()
  const gone = Boolean(expense.voided_at || expense.deleted_at)
  const hasActions = Boolean(expense.restock_id) || expense.can.edit || expense.can.delete

  return (
    <RecordListItem
      voided={gone}
      title={formatBusinessDate(expense.occurred_on)}
      badges={
        <>
          <VoidedBadge record={expense} />
          {expense.deleted_at ? <Badge variant="destructive">{t('expenses.deleted')}</Badge> : null}
        </>
      }
      meta={[
        { icon: TagIcon, label: t('expenses.filterCategory'), value: categoryLabel(t, expense.category.name) },
        expense.vendor ? { icon: TruckIcon, label: t('expenses.columns.vendor'), value: expense.vendor } : null,
        expense.created_by
          ? {
              icon: UserIcon,
              label: t('expenses.columns.createdBy'),
              value: expense.created_by.name ?? expense.created_by.email,
            }
          : null,
      ]}
      description={expense.note ?? undefined}
      figure={`Rp ${formatMoney(expense.amount)}`}
      actions={
        hasActions ? (
          <>
            {expense.restock_id ? (
              <Button asChild variant="ghost" size="sm">
                <Link
                  to="/w/$slug/materials/restocks/$restockId"
                  params={{ slug, restockId: String(expense.restock_id) }}
                >
                  {t('expenses.openRestock')}
                </Link>
              </Button>
            ) : null}
            {expense.can.edit ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/w/$slug/expenses/$expenseId/edit" params={{ slug, expenseId: String(expense.id) }}>
                  {t('expenses.edit')}
                </Link>
              </Button>
            ) : null}
            {expense.can.delete ? (
              <DeleteExpenseDialog
                pending={remove.isPending}
                errorMessage={remove.isError ? expenseErrorMessage(t, remove.error) : ''}
                onReset={() => remove.reset()}
                onDelete={(reason, done) =>
                  remove.mutate(
                    { params: { path: { slug, id: expense.id }, query: { reason } } },
                    { onSuccess: done },
                  )
                }
              />
            ) : null}
          </>
        ) : undefined
      }
    />
  )
}
